import { useNavigate } from 'react-router-dom';
import { Modal } from '@react-apps/design-system';
import { useContractBillingAgreements } from '../hooks/use-contract-details';
import {
  BillingAgreementStatusBadge,
  ErrorState,
  Mono,
  SkeletonCard,
  billingAgreementModeLabel,
  formatCurrency,
  formatDateTime,
} from '../shared/display';

/**
 * Lista todas as BAs que já pertenceram ao contrato (atual + substituídas por
 * renegociação) e leva à página de cada uma. Existe porque a aba "Billing
 * Agreement" só mostra a BA **atual**: num contrato renegociado, o histórico das
 * anteriores — e as invoices substituídas/canceladas delas — não tinha como ser
 * alcançado pela tela.
 */
export function ContractBillingAgreementsModal({
  contractId,
  currentBillingAgreementId,
  open,
  onClose,
}: {
  contractId: string;
  currentBillingAgreementId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useContractBillingAgreements(
    contractId,
    open,
  );

  const billingAgreements = data?.billingAgreements ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Acordos de pagamento deste contrato"
      size="lg"
    >
      {isLoading ? (
        <SkeletonCard rows={3} />
      ) : isError ? (
        <ErrorState message="Não foi possível carregar os acordos de pagamento." />
      ) : billingAgreements.length === 0 ? (
        <p className="text-(--color-text-secondary) text-sm italic">
          Nenhum acordo de pagamento neste contrato.
        </p>
      ) : (
        <ul className="space-y-2">
          {billingAgreements.map((ba) => (
            <li key={ba.id}>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  void navigate(`/billing-agreements/${ba.id}`);
                }}
                className="border-(--color-border) hover:border-(--color-accent) hover:bg-(--color-bg-secondary) w-full rounded-lg border p-3 text-left transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <Mono>{ba.id}</Mono>
                  <div className="flex shrink-0 items-center gap-2">
                    {ba.id === currentBillingAgreementId && (
                      <span className="text-(--color-accent) text-xs font-medium">
                        (atual)
                      </span>
                    )}
                    <BillingAgreementStatusBadge status={ba.status} />
                  </div>
                </div>
                <div className="text-(--color-text-secondary) mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                  <span>{billingAgreementModeLabel(ba.mode)}</span>
                  <span>{formatCurrency(ba.totalPrice)}</span>
                  <span>Criada em {formatDateTime(ba.createdAt)}</span>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
